"use client";

import { colors, util } from "@/common/theme";
import NineGLImageElement from "@/components/gl/NineGLImage/NineGLImageElement";
import { TransitionLink } from "@/mono/components/TransitionLink";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import SplitText from "mono/components/SplitText";

import { events } from "@/common/constants";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { map } from "@/mono/lib/math";
import useEvent from "mono/lib/hooks/useEvent";
import emitter from "@/mono/lib/emitter";
import { useGlobalStore } from "@/mono/store/global";

import { create } from "zustand";

export const showreelDimensions = create(() => [0, 0]);

const HeroVideo = ({ track, ...props }) => {
  const videoRef = useRef();
  const textureReadyRef = useRef(false);
  const transitionCompleteRef = useRef(false);
  const hasAnimatedRef = useRef(false);
  const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);
  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  const videoSrc = isMobileLayout
    ? "/video/showreel_mobile.mp4"
    : "/video/showreel.mp4";

  const _p = useRef(0);

  useScrollRigScrollTrigger({
    el: track,
    update: noWebGLImages
      ? undefined
      : (v, scrollState) => {
          _p.current = scrollState.progress;

          let scale = map(_p.current, 0.7, 1, 1, 0.7, true);

          if (videoRef.current?.group) {
            videoRef.current.group.scale.setScalar(scale);
          }
        },
  });

  function tryAnimateIn() {
    if (hasAnimatedRef.current) return;
    if (!transitionCompleteRef.current) return;
    if (!noWebGLImages && !textureReadyRef.current) return;
    hasAnimatedRef.current = true;

    videoRef.current?.animateIn?.();
    videoRef.current?.playVideo?.();
  }

  // Initial page load: loader completes
  useEvent(events.LOADING_OUT_COMPLETE, () => {
    transitionCompleteRef.current = true;
    tryAnimateIn();
  });

  // Page navigation: transition completes
  useEvent("HOME:ON_ENTERING", ({ from }) => {
    if (from) {
      transitionCompleteRef.current = true;
      tryAnimateIn();
    }
  });

  return (
    <NineGLImageElement
      ref={videoRef}
      className="home-hero__gl"
      isVideo
      src={videoSrc}
      animateOnScroll={false}
      useHover={false}
      debug={true}
      onTextureReady={() => {
        textureReadyRef.current = true;
        emitter.emit("HOME:PAGE_READY");
        tryAnimateIn();
      }}
      {...props}
    />
  );
};

export default function HomeHero() {
  const track = useRef();
  const ref = useRef();
  const ctaHeader = useRef();
  const ctaParagraph = useRef();

  useEffect(() => {
    ctaHeader.current?.animateIn();
    ctaParagraph.current?.animateIn();

    ref.current?.animateIn();
  }, []);

  return (
    <StyledHero ref={track} className="home-hero">
      <HeroVideo track={track} />
      <HeroCTAContainer>
        {/* <SplitText
          ref={ctaHeader}
          tag="h1"
          animateOnScroll={false}
          animation="charDoubleClipped"
          type="chars"
        >
          Nine
        </SplitText> */}
        {/* <SplitText ref={ctaParagraph} tag="h3" animateOnScroll={false}>
          Creative agency
        </SplitText>
        <TransitionLink href="/what-we-do" className="hero__button">
          <span>What we do</span>
        </TransitionLink> */}
      </HeroCTAContainer>
    </StyledHero>
  );
}

const StyledHero = styled.section`
  position: relative;
  ${util.heroSizing}

  margin-bottom: 150rem;

  .home-hero__gl {
    width: 100%;
    height: 100%;
  }

  h1 {
    color: ${colors.nevada};
    font-size: 1000rem;
    letter-spacing: -45rem;
  }
`;

const HeroCTA = styled.div`
  margin-top: 20rem;
  margin-left: 500rem;

  h3 {
    word-spacing: 100vw;
    line-height: 80rem;
  }

  p {
    margin-top: 40rem;
    max-width: 800rem;
  }

  .hero__button {
    margin-top: 40rem;
    display: flex;
    align-items: center;

    span {
      ${({ theme: { locale } }) => setTypography("smallParagraph", locale)};
      text-transform: none;
      margin-left: 30rem;
    }
  }
`;

const HeroCTAContainer = styled(HeroCTA)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  pointer-events: none;
  z-index: 2;

  .hero__button {
    pointer-events: auto;
  }
`;
